/**
 * Badge de status + flecha de tendencia comparando contra el período anterior.
 * Renderiza el valor formateado, color por status y delta porcentual.
 */
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { compareValues, computeStatus, type KpiDefinition } from "@axorial/shared";

export function MetricStatusBadge({
  def,
  current,
  previous,
  showValue = true,
}: {
  def: Pick<
    KpiDefinition,
    | "direction"
    | "unit"
    | "thresholdExcellent"
    | "thresholdGood"
    | "thresholdImprovable"
    | "thresholdBad"
  >;
  current: number | null | undefined;
  previous?: number | null;
  showValue?: boolean;
}) {
  const status = computeStatus(current ?? null, def);
  const { delta, percentDelta, trendIsBetter } = compareValues(
    current,
    previous ?? null,
    def.direction,
  );

  const colorClass =
    status === "excellent"
      ? "text-info"
      : status === "good"
        ? "text-success"
        : status === "improvable"
          ? "text-warning"
          : status === "bad"
            ? "text-error"
            : "text-base-content/50";

  const trendIcon =
    delta == null ? (
      <Minus className="size-3" />
    ) : delta > 0 ? (
      <ArrowUpRight className="size-3" />
    ) : delta < 0 ? (
      <ArrowDownRight className="size-3" />
    ) : (
      <Minus className="size-3" />
    );

  const trendColor =
    trendIsBetter == null ? "text-base-content/40" : trendIsBetter ? "text-success" : "text-error";

  return (
    <div className="inline-flex items-center gap-2">
      {showValue && (
        <span className={`font-mono tabular-nums font-semibold ${colorClass}`}>
          {current == null ? "—" : formatValue(current, def.unit)}
        </span>
      )}
      {previous != null && current != null && (
        <span className={`inline-flex items-center gap-0.5 text-xs ${trendColor}`}>
          {trendIcon}
          {percentDelta == null ? "" : `${Math.abs(percentDelta).toFixed(1)}%`}
        </span>
      )}
    </div>
  );
}

function formatValue(v: number, unit: string | null | undefined): string {
  const fixed = Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2);
  const trimmed = fixed.replace(/\.?0+$/, "");
  if (unit === "%") return `${trimmed}%`;
  if (unit === "MXN")
    return `$${Number(trimmed).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
  return unit ? `${trimmed} ${unit}` : trimmed;
}
